import React, { useMemo } from 'react';
import { Layout, Row, Col, Typography } from 'antd';
import { Header } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import { 
  faMoneyBillWave, 
  faClock,
  faCalendarAlt,
  faCalendarWeek
} from '@fortawesome/free-solid-svg-icons';
import { useFinanceDashboard } from '../../hooks/useFinanceDashboard';
import DashboardStats from '../../components/finance-dashboard/DashboardStats';
import ProgressChart from '../../components/finance-dashboard/ProgressChart';
import DataTable from '../../components/finance-dashboard/DataTable';
import DashboardState from '../../components/finance-dashboard/DashboardState';
import { formatCurrency } from '../../utils/currencyUtils';

const { Content } = Layout;
const { Title } = Typography;

const FinanceDashboardPage: React.FC = () => {
  const {
    loading,
    error,
    metrics,
    categoryData,
    yearData,
    subcategoryData,
    l3l4Data,
    refetch
  } = useFinanceDashboard();

  // Memoized stats data
  const stats = useMemo(() => {
    if (!metrics) return [];

    return [
      { 
        title: 'Total Amount', 
        value: formatCurrency(metrics.totalAmount), 
        icon: faMoneyBillWave, 
        link: '/finance/expenses',
        color: 'text-green-500'
      },
      { 
        title: 'Last 3 Months', 
        value: formatCurrency(metrics.last3MonthsAmount), 
        icon: faClock, 
        link: `/finance/expenses?date=[${dayjs().subtract(3, 'months').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')},inRange]`,
        color: 'text-blue-500'
      },
      { 
        title: 'Last 6 Months', 
        value: formatCurrency(metrics.last6MonthsAmount), 
        icon: faCalendarWeek, 
        link: `/finance/expenses?date=[${dayjs().subtract(6, 'months').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')},inRange]`,
        color: 'text-purple-500'
      },
      { 
        title: 'Last 9 Months', 
        value: formatCurrency(metrics.last9MonthsAmount), 
        icon: faCalendarAlt, 
        link: `/finance/expenses?date=[${dayjs().subtract(9, 'months').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')},inRange]`,
        color: 'text-orange-500'
      },
    ];
  }, [metrics]);

  // Memoized category progress data with links
  const categoryProgressData = useMemo(() => {
    if (!categoryData?.top3Categories) return [];

    return categoryData.top3Categories.map((category: any) => ({
      ...category,
      link: `/finance/expenses?catL1=[${category.name},equals]`
    }));
  }, [categoryData]);

  // Memoized year progress data with links
  const yearProgressData = useMemo(() => {
    return yearData.map((yearData: any) => {
      const startDate = `${yearData.year}-01-01`;
      const endDate = `${yearData.year}-12-31`;
      return {
        ...yearData,
        name: yearData.year.toString(),
        link: `/finance/expenses?date=[${startDate},${endDate},between]`
      };
    });
  }, [yearData]);

  // Memoized subcategory data with proper links
  const nakipurLandData = useMemo(() => {
    return subcategoryData?.nakipurLand?.map((subcategory: any) => ({
      ...subcategory,
      link: `/finance/expenses?catL1=[Nakipur Land,equals]&catL2=[${subcategory.name},equals]`
    })) || [];
  }, [subcategoryData]);

  const projectData = useMemo(() => {
    return subcategoryData?.project?.map((subcategory: any) => ({
      ...subcategory,
      link: `/finance/expenses?catL1=[Project,equals]&catL2=[${subcategory.name},equals]`
    })) || [];
  }, [subcategoryData]);

  const softwareData = useMemo(() => {
    return subcategoryData?.software?.map((subcategory: any) => ({
      ...subcategory,
      link: `/finance/expenses?catL1=[Software,equals]&catL2=[${subcategory.name},equals]`
    })) || [];
  }, [subcategoryData]);

  return (
    <Layout className="min-h-screen bg-white">
      <Header className="bg-white px-6 sticky top-0 z-50 border-b border-gray-200">
        <Title level={3} className="m-0">Finance Dashboard</Title>
      </Header>

      <Content className="px-2"> 
        <DashboardState 
          loading={loading} 
          error={error} 
          onRetry={refetch} 
        />

        {!loading && !error && (
          <>
            {/* Stats Cards */}
            <DashboardStats stats={stats} />

            {/* Progress Charts Row */}
            <Row gutter={[24, 24]} className="mt-6">
              <Col xs={24} md={12}>
                <ProgressChart
                  title="Category Distribution"
                  data={categoryProgressData}
                  colors={['#3B82F6', '#8B5CF6', '#F59E0B']}
                />
              </Col>
              <Col xs={24} md={12}>
                <ProgressChart
                  title="Year-wise Distribution"
                  data={yearProgressData}
                  colors={['#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316']}
                />
              </Col>
            </Row>

            {/* Data Tables Row */}
            <Row gutter={[24, 24]} className="mt-10">
              <Col xs={24} md={12}>
                <DataTable
                  title="Nakipur Land Subcategories"
                  data={nakipurLandData}
                />
              </Col>
              <Col xs={24} md={12}>
                <DataTable
                  title="Project Subcategories"
                  data={projectData}
                />
              </Col>
            </Row>

            <Row gutter={[24, 24]} className="mt-6">
              <Col xs={24} md={12}>
                <DataTable
                  title="Software Subcategories"
                  data={softwareData}
                />
              </Col>
              <Col xs={24} md={12}>
                <DataTable
                  title="Category L3 & L4 Details"
                  data={l3l4Data}
                />
              </Col>
            </Row>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default FinanceDashboardPage;
